import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { SyncedSubtitles } from '../components/SyncedSubtitles';
import { PrixiaLogo } from '../components/PrixiaLogo';

const DUR = SCENES.HERO.duration;

export const HeroScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeIn = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 20, DUR], [1, 0], { extrapolateRight: 'clamp' });

  const revealScale = spring({
    frame: frame - 15,
    fps,
    config: { damping: 10, stiffness: 60, mass: 1 },
  });

  const revealOpacity = interpolate(frame, [15, 35], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const subOpacity = interpolate(frame, [50, 70], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const subY = interpolate(frame, [50, 70], [20, 0], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  // Rings using brand colors
  const ring1 = interpolate(frame, [20, DUR], [0, 360], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const ring2 = interpolate(frame, [25, DUR], [0, -360], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const ringOpacity = interpolate(frame, [20, 45], [0, 0.25], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const features = ['Cotizaciones', 'Modelos', 'Analytics', 'IA', 'Monitoreo', 'ERP'];

  const ringConfigs = [
    { rotation: ring1, size: 520, color: COLORS.brandIndigo, dotColor: COLORS.brandTeal },
    { rotation: ring2, size: 660, color: COLORS.brandTeal, dotColor: COLORS.brandAmber },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeIn * fadeOut }}>
      {/* Background glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at center, ${COLORS.brandIndigo}12 0%, transparent 70%)`,
        }}
      />

      {/* Rotating rings */}
      {ringConfigs.map((cfg, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: cfg.size,
            height: cfg.size,
            borderRadius: '50%',
            border: `1px solid ${cfg.color}50`,
            transform: `translate(-50%, -50%) rotate(${cfg.rotation}deg)`,
            opacity: ringOpacity,
          }}
        >
          {[0, 90, 180, 270].map((deg) => (
            <div
              key={deg}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: cfg.dotColor,
                transform: `rotate(${deg}deg) translateX(${cfg.size / 2}px) translate(-50%, -50%)`,
                boxShadow: `0 0 10px ${cfg.dotColor}`,
              }}
            />
          ))}
        </div>
      ))}

      {/* Center content */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -55%) scale(${revealScale})`,
          opacity: revealOpacity,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          zIndex: 10,
        }}
      >
        {/* Real Prixia logo */}
        <PrixiaLogo startFrame={15} scale={2.8} variant="full" />

        {/* Subtitle */}
        <div
          style={{
            fontSize: 28,
            fontWeight: 300,
            color: COLORS.gray,
            fontFamily: 'Inter, system-ui, sans-serif',
            opacity: subOpacity,
            transform: `translateY(${subY}px)`,
            textAlign: 'center',
            lineHeight: 1.5,
            marginTop: 8,
          }}
        >
          Inteligencia de Precios
          <br />
          <span style={{ color: COLORS.brandTeal, fontWeight: 600 }}>Potenciada por IA</span>
        </div>

        {/* Feature pills */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginTop: 16,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {features.map((feat, i) => {
            const pillDelay = 70 + i * 8;
            const pillOpacity = interpolate(frame, [pillDelay, pillDelay + 15], [0, 1], {
              extrapolateRight: 'clamp',
              extrapolateLeft: 'clamp',
            });
            const pillScale = spring({
              frame: Math.max(0, frame - pillDelay),
              fps,
              config: { damping: 15, stiffness: 120 },
            });
            const pillColors = [COLORS.brandIndigo, COLORS.brandTeal, COLORS.brandAmber];
            const pillColor = pillColors[i % 3];

            return (
              <div
                key={feat}
                style={{
                  padding: '10px 24px',
                  borderRadius: 100,
                  border: `1px solid ${pillColor}50`,
                  background: `${pillColor}12`,
                  color: COLORS.white,
                  fontSize: 16,
                  fontWeight: 500,
                  fontFamily: 'Inter, system-ui, sans-serif',
                  opacity: pillOpacity,
                  transform: `scale(${pillScale})`,
                }}
              >
                {feat}
              </div>
            );
          })}
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.HERO} />
    </AbsoluteFill>
  );
};
